/**
 * CommandPalette: the ⌘K dialog's shell. A search field over grouped results; type, arrow, Enter.
 * The caller owns the query and the groups (what matches, in what order) and what choosing does;
 * the shell owns the keyboard, the cursor and the listbox semantics.
 */
import { useEffect, useId, useRef, useState, type KeyboardEvent, type ReactNode } from "react";
import { Dialog as Primitive } from "radix-ui";
import { cx } from "./cx";
import { Icon } from "./icons";
import { Kbd } from "./Kbd";
import { useFocusReturn } from "./overlay";

export interface PaletteItem {
  key: string;
  label: string;
  /** Muted text after the label. */
  hint?: string;
  icon?: ReactNode;
  /** The stretch of `label` the query matched, to mark. */
  match?: { start: number; length: number };
  /** A person's name: carries `data-name`, so captures can mask it. */
  person?: boolean;
}

export interface PaletteGroup {
  id: string;
  label: string;
  items: PaletteItem[];
}

interface CommandPaletteProps {
  open: boolean;
  onClose: () => void;
  /** The dialog's accessible name. */
  label: string;
  placeholder?: string;
  query: string;
  onQueryChange: (query: string) => void;
  /** Empty groups are skipped. */
  groups: PaletteGroup[];
  /** Enter or a click. Close the palette here if the choice leaves it. */
  onChoose: (key: string) => void;
  /** False while results for `query` are still loading: Enter then waits and takes the first result when they land. */
  settled?: boolean;
  /** Shown when nothing matches. */
  empty?: string;
}

function Label({ item }: { item: PaletteItem }) {
  const { label, match } = item;
  return (
    // Room for descenders: at line-height 1 the truncating box clips the foot of a y, g or p.
    <span data-name={item.person || undefined} className="truncate leading-[1.4]">
      {match ? (
        <>
          {label.slice(0, match.start)}
          <mark className="rounded-[2px] bg-highlight text-inherit">{label.slice(match.start, match.start + match.length)}</mark>
          {label.slice(match.start + match.length)}
        </>
      ) : (
        label
      )}
    </span>
  );
}

export function CommandPalette({ open, onClose, label, placeholder = "Search", query, onQueryChange, groups, onChoose, settled = true, empty = "Nothing matches." }: CommandPaletteProps) {
  const id = useId();
  const input = useRef<HTMLInputElement>(null);
  const list = useRef<HTMLDivElement>(null);
  const focus = useFocusReturn({ first: () => input.current });
  const [cursor, setCursor] = useState<string | null>(null);
  const enterPending = useRef(false);

  const shown = groups.filter((group) => group.items.length > 0);
  const items = shown.flatMap((group) => group.items);
  const found = items.findIndex((item) => item.key === cursor);
  const index = found < 0 ? 0 : found;
  const active = items[index];
  const first = items[0]?.key;

  useEffect(() => {
    list.current?.querySelector('[aria-selected="true"]')?.scrollIntoView?.({ block: "nearest" });
  }, [active?.key]);

  // Enter pressed before the results landed: take the first one once they do.
  useEffect(() => {
    if (!settled || !enterPending.current) return;
    enterPending.current = false;
    if (first) onChoose(first);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fires when the results settle, with the list that settled
  }, [settled, first]);

  const move = (step: number) => {
    const next = items[(index + step + items.length) % items.length];
    if (next) setCursor(next.key);
  };

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    const jump: Record<string, () => void> = {
      ArrowDown: () => move(1),
      ArrowUp: () => move(-1),
      Home: () => setCursor(items[0]?.key ?? null),
      End: () => setCursor(items[items.length - 1]?.key ?? null),
      Enter: () => {
        if (!settled) enterPending.current = true;
        else if (active) onChoose(active.key);
      },
    };
    const act = jump[event.key];
    if (!act) return;
    event.preventDefault();
    act();
  };

  return (
    <Primitive.Root open={open} onOpenChange={(next) => !next && onClose()}>
      <Primitive.Portal>
        <Primitive.Content
            data-slot="command-palette"
            aria-label={label}
            aria-describedby={undefined}
            {...focus}
            className="fixed inset-x-0 top-[14vh] z-50 mx-auto flex w-[calc(100%-32px)] max-w-[600px] flex-col rounded-xl bg-muted p-1 shadow-popover outline-none data-[state=open]:animate-menu-in data-[state=closed]:animate-menu-out"
          >
            <Primitive.Title className="sr-only">{label}</Primitive.Title>
            <div className="flex h-12 shrink-0 items-center gap-2.5 px-3">
              <Icon name="search" size={18} />
              <input
                ref={input}
                type="text"
                role="combobox"
                aria-label={label}
                aria-expanded="true"
                aria-controls={`${id}-list`}
                aria-activedescendant={active ? `${id}-${active.key}` : undefined}
                aria-autocomplete="list"
                placeholder={placeholder}
                autoComplete="off"
                spellCheck={false}
                value={query}
                onChange={(event) => {
                  enterPending.current = false;
                  setCursor(null);
                  onQueryChange(event.target.value);
                }}
                onKeyDown={onKeyDown}
                className="h-9 min-w-0 flex-1 bg-transparent text-title leading-none text-foreground outline-none placeholder:text-muted-foreground"
              />
              <Kbd>Esc</Kbd>
            </div>
            <div ref={list} id={`${id}-list`} role="listbox" aria-label="Results" className="flex max-h-[min(420px,60vh)] flex-col gap-2 overflow-y-auto rounded-xl border-hair border-input bg-card p-1 [scrollbar-width:thin]">
              {shown.map((group) => (
                <div key={group.id} role="group" aria-labelledby={`${id}-g-${group.id}`} className="flex flex-col">
                  <div id={`${id}-g-${group.id}`} className="px-2.5 pb-1.5 pt-2 text-caption font-medium uppercase leading-none tracking-[0.04em] text-muted-foreground">
                    {group.label}
                  </div>
                  {group.items.map((item) => (
                    // Keys are handled by the combobox input; options are never tabbed to.
                    // eslint-disable-next-line jsx-a11y/click-events-have-key-events
                    <div
                      key={item.key}
                      id={`${id}-${item.key}`}
                      role="option"
                      tabIndex={-1}
                      aria-selected={item.key === active?.key}
                      onPointerMove={() => setCursor(item.key)}
                      // A click chooses; focus stays in the field, so typing goes on filtering.
                      onMouseDown={(event) => event.preventDefault()}
                      onClick={() => onChoose(item.key)}
                      className="group/opt flex h-9 cursor-default select-none items-center gap-2.5 rounded-lg px-2.5 text-body leading-none text-secondary-foreground transition-colors aria-selected:bg-accent aria-selected:text-foreground"
                    >
                      {item.icon}
                      <Label item={item} />
                      {item.hint && <span className="ml-auto shrink-0 pl-3 text-meta text-muted-foreground transition-colors group-aria-selected/opt:text-secondary-foreground">{item.hint}</span>}
                    </div>
                  ))}
                </div>
              ))}
              {items.length === 0 && <p className={cx("px-2 py-6 text-center text-body leading-none text-muted-foreground", !settled && "opacity-0")}>{empty}</p>}
            </div>
          </Primitive.Content>
      </Primitive.Portal>
    </Primitive.Root>
  );
}
